import cloneDeep from 'lodash-es/cloneDeep';
import { computed, ref, watch } from 'vue';

import { CompanyPermissions } from '@/models/permission';
import { SplitName, SplitTreatment, SplitValue } from '@/models/split';
import { SeedToSalePermissions } from '@/models/traceability';
import {
  InternalUserPermissions,
  PartialUserSettings,
  UserSettings,
  UserSettingsByPage,
  UserSettingsByUser,
} from '@/models/user';
import { MergeWith } from '@/models/utils';
import { addSentryUserData } from '@/sentry';
import { loadDelightedSurvey } from '@/services/analytics/delighted';
import { loadSurfsideSegments } from '@/services/analytics/surfside';
import { GetUserApiResponse } from '@/services/api/models/user.models';
import { initZendeskChat } from '@/services/support/zendesk';
import hashCode from '@/utils/hashCode';
import { getUserSettingsByPageStorage, getUserSettingsByUserStorage } from '@/utils/storage';

import { useCompanyStore } from '../company/company.store';

/**
 * Represents the actual data we parse and store in state after we fetch
 * the user from our API.  This model is very similar to the API response.
 * @see GetUserApiResponse /services/api/models/user.models.ts
 */
export type InternalUserState = MergeWith<
  GetUserApiResponse,
  {
    algoliaQueryId: string;
    permissions: InternalUserPermissions;
    /** Settings scoped to the location pathname */
    settings: UserSettingsByPage;
    /** Settings scoped to the user id */
    settingsByUser: UserSettingsByUser;
    splits?: SplitTreatment;
    id: number | null;
    featuresCount?: number;
    lastLogin: string | null;
    referralCode?: string;
    zendeskChatJwt?: string;
  }
>;

export function getInitialS2SPermissionsState(): SeedToSalePermissions {
  return {
    canAccessSeedToSaleIntegration: false,
    canAccessSeedToSalePackages: false,
    canAccessSeedToSalePage: false,
    canAccessSeedToSaleProducts: false,
    canAccessSeedToSaleTransfers: false,
    canSplitPackages: false,
  };
}

export const setupUserState = () => {
  const companyStore = useCompanyStore();

  const userState = ref<InternalUserState>({
    activeCompanyId: null,
    adminId: null,
    algoliaQueryId: '',
    companies: [],
    dateJoined: '',
    dateJoinedVerbose: '',
    dateModalDismissed: null,
    dateSignedTc: null,
    email: '',
    featureFlags: {
      enableAccountOwnership: false,
      enableBMEApplications: false,
      enableBuyerApplications: false,
      enableBuyerDeliveryPreferences: false,
      enableBuyerIntegrations: false,
      enableL3OrderStatusSync: false,
      enableL3OrderStatusSyncBanners: false,
      enableLlpEmailService: false,
      enableLogistics: false,
      enableMetrcTransferTemplate: false,
      enablePaymentPortalNavItem: false,
      enableSellerApplications: false,
      enableServicesMarketplace: false,
      enableShipAssist: false,
      enableStaffLimitedAccess: false,
      envName: '',
      financialPortalUrl: '',
      referralProgramActive: false,
      showFeatureHighlights: false,
    },
    firstName: '',
    hashedActiveCompany: '',
    hasApiAccess: false,
    hasPurchaseLists: false,
    hasS2SEnabled: false,
    headshot: '',
    id: null,
    isBuyer: false,
    isLeaflinkStaff: false,
    isSeller: false,
    isSuperuser: false,
    language: '',
    lastLogin: '',
    lastName: '',
    onlySmp: false,
    permissions: {
      seedToSale: getInitialS2SPermissionsState(),
    },
    phone: '',
    platformAccess: {
      access: {
        commercePortal: true,
        paymentsPortal: false,
      },
    },
    referralCode: '',
    settings: getUserSettingsByPageStorage(),
    settingsByUser: getUserSettingsByUserStorage(),
    splits: undefined,
    statesAbbr: '',
    username: '',
    zendeskChatJwt: undefined,
  });

  const isUserAuthenticated = ref(false);

  const companies = computed(() => userState.value.companies);
  const sellers = computed(() => companies.value.filter((company) => company.seller));
  const buyers = computed(() => companies.value.filter((company) => company.buyer));

  const getIsSplitEnabled = computed(() => (splitName: SplitName, allowedValue: SplitValue | SplitValue[] = 'on') => {
    let splitTreatmentValue = getSplitTreatment.value(splitName);

    if (!splitTreatmentValue) {
      return false;
    }

    /**
     * splitTreatmentValue can be an array if the split treatment returns JSON via Dynamic Configuration: [treatmentValue, json]
     * See _call_splitio_client where treatments are assigned on the backend (after `get_treatments_with_config`)
     * @see: https://github.com/LeafLink/leaflink/blob/main/leaflink/libs/splitio.py
     * @see: https://www.split.io/product/dynamic-configuration/
     */
    splitTreatmentValue = Array.isArray(splitTreatmentValue) ? splitTreatmentValue[0] : splitTreatmentValue;
    const allowedValues = Array.isArray(allowedValue) ? allowedValue : [allowedValue];

    return allowedValues.includes(splitTreatmentValue);
  });

  const getSplitTreatment = computed(() => (splitName: SplitName) => {
    return userState.value.splits?.[splitName];
  });

  const getEnabledSplits = computed(() => () => {
    const enabledSplits: SplitTreatment = cloneDeep(userState.value.splits) || ({} as SplitTreatment);

    Object.entries(enabledSplits).forEach(([key, value]) => {
      const treatmentValue = Array.isArray(value) ? value[0] : value;

      if (treatmentValue === 'control' || treatmentValue === 'off') {
        delete enabledSplits[key];
      }
    });

    return enabledSplits;
  });

  /**
   * Returns a user setting from state for a specific location pathname.
   */
  const getUserSettingsByPage = computed(
    () =>
      <K extends keyof UserSettings>(
        name: K,
        options: {
          /** @deprecated - use setPersistentItem and getPersistentItem from Stash with the `global: true` option */
          global?: boolean;
        } = { global: false },
      ): PartialUserSettings<K> | undefined => {
        let settings: PartialUserSettings<K> | undefined;

        if (options?.global) {
          settings = userState.value.settings[name] as PartialUserSettings<K>;
        } else {
          // remove trailing slash from the path to avoid duplicate settings
          const path = location.pathname.replace(/\/$/, '');
          const key = hashCode(path);
          settings = userState.value.settings?.[name]?.[key];
        }

        return settings;
      },
  );

  const getUserSettingsByUser = computed(() =>
    /**
     * Get a user setting for a specific user.
     * @param name - The name of the setting to get.
     * @param [userId] - The user id to get the setting for. Defaults to the current user.
     */
    <K extends keyof UserSettings>(name: K, userId?: number): PartialUserSettings<K> | undefined => {
      const user = userId || userState.value.id;
      if (!user) {
        throw new Error('A valid user id is required to get user settings');
      }

      const key = hashCode(user.toString());
      return userState.value.settingsByUser?.[key]?.[name];
    },
  );

  const isReplenishmentEnabled = computed(() => {
    if (!userActiveCompany.value) {
      return false;
    }

    return Boolean(userActiveCompany.value.group?.enableReplenishment);
  });

  const userActiveCompany = computed(() => {
    const activeCompanyId = companyStore.activeCompanyId || userState.value.activeCompanyId;

    if (!activeCompanyId) {
      return;
    }

    return userState.value.companies.find(({ id }) => id === activeCompanyId);
  });

  const userActiveCompanyGroup = computed(() => {
    return userActiveCompany.value?.group
      ? userState.value.companies.filter((company) => company.group?.id === userActiveCompany.value?.group.id)
      : [];
  });

  const userActiveCompanyPermissions = computed<CompanyPermissions | null>(() => {
    if (!userActiveCompany.value) {
      return null;
    }

    return {
      enableTerritories: userActiveCompany.value.enableTerritories,
      enableTraceabilityService: userActiveCompany.value.enableTraceabilityService,
      enableZoneFeatures: userActiveCompany.value.enableZoneFeatures,
      staffHasLimitedAccess: userActiveCompany.value.staffHasLimitedAccess,
      noLimitedAccess: !userActiveCompany.value.staffHasLimitedAccess,
      staffIsAdmin: userActiveCompany.value.staffIsAdmin,
      canAccessShippingDashboard: !userActiveCompany.value.isLogisticsSplashVisible,
      enableLogisticsForCompany: userActiveCompany.value.enableLogistics, // this variable is renamed in order to avoid conflict in the validatePermissions function (it conflicts with user.featureFlags.enableLogistics)
      enableMetrcTransferTemplateCompany: userActiveCompany.value.enableLogisticsMetrcTransferTemplate,
      showPaid: userActiveCompany.value.showPaid,
      enableInternalApprovals: userActiveCompany.value.enableInternalApprovals,
      enableOrdersReceivedNew: userActiveCompany.value.enableOrdersReceivedNew,
      enableOrderUploads: userActiveCompany.value.enableOrderUploads,
      enablePackingApp: userActiveCompany.value.enablePackingApp,
      enableDeliveries: userActiveCompany.value.enableDeliveries,
      allowSameDayDeliveriesOnly: userActiveCompany.value.allowSameDayDeliveriesOnly,
      disablePickupDatetimes: userActiveCompany.value.disablePickupDatetimes,
      staffIsAccountOwner: userActiveCompany.value.staffIsAccountOwner,
      canAccessRetailerInsights: userActiveCompany.value.enableAdvancedReportingTableauRetailer,
      canAccessReplenishmentReportingTableau:
        !!userActiveCompany.value.group?.enableAdvancedReportingTableauReplenishment,
      canAccessAssignOrders: !userActiveCompany.value.isLogisticsOnly,
      enableRetailerCategoryManagement: userActiveCompany.value.enableRetailerCategoryManagement,
      enableAdvancedReportingSigmaCatman: userActiveCompany.value.enableAdvancedReportingSigmaCatman,
      enableParInlineInsightsCatman: userActiveCompany.value.enableParInlineInsightsCatman,
      enableProductLeaderboardCatman: userActiveCompany.value.enableProductLeaderboardCatman,

      // staff permissions
      ...userActiveCompany.value.staffPermissions,
      seedToSale: userState.value.permissions.seedToSale,
      canAccessDeliveries:
        userActiveCompany.value.enableDeliveries && userActiveCompany.value.staffPermissions.canAccessDeliveries,
      canAccessLogisticsDashboard:
        userActiveCompany.value.staffPermissions.canAccessLogistics && userActiveCompany.value.isLogisticsVisible,
    };
  });

  watch(
    () => userState.value.activeCompanyId,
    () => {
      if (userState.value.activeCompanyId) {
        addSentryUserData();
        loadDelightedSurvey();
        initZendeskChat();
        loadSurfsideSegments({
          isBuyer: userState.value.isBuyer,
          isSeller: userState.value.isSeller,
        });
      }
    },
    { immediate: true },
  );

  return {
    companies,
    buyers,
    sellers,
    getIsSplitEnabled,
    getSplitTreatment,
    getEnabledSplits,
    getUserSettingsByPage,
    getUserSettingsByUser,
    isReplenishmentEnabled,
    user: userState,
    userActiveCompany,
    userActiveCompanyGroup,
    userActiveCompanyPermissions,
    isUserAuthenticated,
  };
};

export type UserState = ReturnType<typeof setupUserState>;
